import React, { useRef, useState } from 'react'
import { Button, CircularProgress, Dialog, DialogActions, DialogContent } from '@material-ui/core'
import { useSnackbar } from 'notistack5'
import { useQueryClient } from 'react-query'
import SampleDetailsPanel from 'src/views/VariantDashboard/SampleDetailsPanel'
import SampleToolDialogTitle from './SampleToolDialogTitle'

// Sample details (phenotype, diseases, notes) for one sample, opened from the
// Sample column of the Home analysis table. The panel, and so its requests,
// mounts only while the dialog is open.
const SampleDetailsDialog = ({ open, onClose, fileId, sampleName, sampleLabel }) => {
  const panelRef = useRef(null)
  const [canSave, setCanSave] = useState(false)
  const [saving, setSaving] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const queryClient = useQueryClient()

  const handleClose = () => {
    if (!saving) onClose()
  }

  const handleSave = async () => {
    if (!panelRef.current || saving) return
    setSaving(true)
    let saved = false
    try {
      saved = await panelRef.current.save()
    } catch (error) {
      console.error('Error saving details:', error)
    }
    setSaving(false)
    if (saved) {
      enqueueSnackbar('Sample details saved', { variant: 'success' })
      onClose()
    } else {
      enqueueSnackbar('Sample details could not be saved. Try again.', { variant: 'error' })
    }
  }

  // The HPO list and file details stay cached after the panel unmounts, and the
  // HPO hook writes back whatever it loads from cache on the next mount. Drop
  // both once the dialog has closed so a reopen always starts from the server.
  const clearCachedDetails = () => {
    setCanSave(false)
    queryClient.removeQueries(['hpo-id-list', fileId], { exact: true })
    queryClient.removeQueries(['file-details', fileId], { exact: true })
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="lg"
      TransitionProps={{ onExited: clearCachedDetails }}
    >
      <SampleToolDialogTitle
        title="Sample details"
        sampleName={sampleLabel}
        onClose={handleClose}
        disableClose={saving}
      />
      <DialogContent sx={{ pt: 0 }}>
        <SampleDetailsPanel ref={panelRef} fileId={fileId} sampleName={sampleName} onCanSaveChange={setCanSave} />
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Save details
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default SampleDetailsDialog
